import { Link } from 'react-router-dom';
import EventCard from '../components/EventCard';
import Countdown from '../components/Countdown';
import OfflinePill from '../components/OfflinePill';
import PhotoTile from '../components/PhotoTile';
import { SkeletonEvents } from '../components/Skeleton';
import { useScheduleModel } from '../lib/schedule';
import { useMedia, visibleTo } from '../lib/media';
import { SUIT_MARK, useSession } from '../lib/session';
import { dayKey, fmtDay, untilLabel } from '../lib/time';

/**
 * Now — the landing screen and the most-used one.
 * NOT on the canvas: the "try next" line asks for Now and the map to be drawn,
 * so rather than invent a new visual language this page is assembled from
 * components the artboards already settled — the hero event card from 1d, the
 * tile from 1h. Worth redrawing properly before November.
 */
export default function Now() {
  const model = useScheduleModel();
  const session = useSession();
  const media = useMedia();
  const shared = visibleTo(media);

  if (model.loading) return <div className="page page-narrow"><SkeletonEvents rows={2} /></div>;

  const focus = model.current ?? model.next;
  const todayRest = model.events.filter(
    (e) => dayKey(e.start) === dayKey(model.at) && e.id !== focus?.id && model.stateOf(e) !== 'past',
  );

  return (
    <div className="page page-narrow">
      <OfflinePill fetchedAt={model.fetchedAt} />

      {/* Removes itself on 5 November — see `Countdown`. Deliberately above the
          team tag and the hero card: before the week it is the reason to open
          the page, and during the week it is gone, so it costs that space
          nothing. */}
      <Countdown />

      {session && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '14px 18px 0' }}>
          <span className="tag tag-accent-2" style={{ gap: 6 }}>
            <span aria-hidden="true">{SUIT_MARK[session.team]}</span> Team {session.team}
          </span>
        </div>
      )}

      {focus ? (
        <>
          <div className="day-head">
            <h4>{model.current ? 'Right now' : 'Up next'}</h4>
            <span className="day-note">{fmtDay(focus.start)}</span>
          </div>
          <div className="ev-list">
            <EventCard
              event={focus}
              venue={model.venueOf(focus.venueId)}
              state={model.stateOf(focus)}
              at={model.at}
              density="hero"
            />
          </div>
          {focus.notes && (
            <div className="page-pad" style={{ paddingTop: 14 }}>
              <h5 style={{ margin: '0 0 5px' }}>Notes from us</h5>
              <p style={{ fontSize: 13, margin: 0, opacity: 0.85 }}>{focus.notes}</p>
            </div>
          )}
        </>
      ) : (
        <div className="empty">
          <div className="mark" aria-hidden="true">♥</div>
          <h4 style={{ margin: 0 }}>That's the week</h4>
          <p>
            Thank you for coming all this way. Everything anybody photographed is still here, and it is
            staying here — have a look through it when you land.
          </p>
          <Link className="btn btn-primary" to="/gallery">Open the gallery</Link>
        </div>
      )}

      {todayRest.length > 0 ? (
        <>
          <div className="day-head"><h4>Later today</h4></div>
          <div className="ev-list">
            {todayRest.map((event) => (
              <EventCard
                key={event.id} event={event} venue={model.venueOf(event.venueId)}
                state={model.stateOf(event)} at={model.at} density="row"
              />
            ))}
          </div>
        </>
      ) : focus && model.stateOf(focus) !== 'now' && (
        <p className="page-pad" style={{ fontSize: 13, opacity: 0.6, marginTop: 16 }}>
          Nothing else today — the next thing is {fmtDay(focus.start)}, {untilLabel(focus.start, model.at)}.
        </p>
      )}

      {shared.length > 0 && (
        <>
          <div className="day-head" style={{ marginTop: 8 }}>
            <h4>Coming in</h4>
            <Link to="/gallery" style={{ fontSize: 12, textDecoration: 'none' }}>{shared.length} photos so far</Link>
          </div>
          <div className="strip">
            {shared.slice(0, 12).map((item) => <PhotoTile key={item.id} item={item} />)}
          </div>
        </>
      )}

      <div className="tz-note">All times Khao Lak, GMT+7</div>
    </div>
  );
}
